import InstagramIcon from "src/images/social/instagram-color.svg";
import InstagramStoryIcon from "src/images/social/instagram-story-color.svg";
import TikTokIcon from "src/images/social/tiktok-color.svg";
import YouTubeIcon from "src/images/social/youtube-color.svg";
import XIcon from "src/images/social/x.svg";
import DiscordIcon from "src/images/social/discord.svg";
import TwitchIcon from "src/images/social/twitch.svg";
import SnapchatIcon from "src/images/social/snapchat.svg";
import FacebookIcon from "src/images/social/facebook-color.svg";
import TelegramIcon from "src/images/social/telegram.svg";
import LinkedInIcon from "src/images/social/linkedin.svg";
import LinkIcon from "src/images/link.svg";
import { ReactNode } from "react";

export interface ErrorMessageAndField {
  field: string;
  message: string;
}

export interface ReduxError {
  field?: string;
  message: string;
  status: number;
}

export interface ReduxAction<T> {
  type: string;
  payload: T;
}

/* SOCIAL PROVIDERS DECLARATION BELOW */
export enum SocialProvider {
  Instagram = "instagram",
  TikTok = "tiktok",
  Youtube = "youtube",
  Twitter = "twitter",
  Snapchat = "snapchat",
  Discord = "discord",
  Twitch = "twitch",
  Facebook = "facebook",
  Telegram = "telegram",
  Linkedin = "linkedin",
  Custom = "custom",
}

export const CRAWLABLE_USER_SOCIAL_PROVIDERS = [
  SocialProvider.Instagram,
  SocialProvider.TikTok,
  SocialProvider.Youtube,
  SocialProvider.Snapchat,
  SocialProvider.Facebook,
];

export const SOCIAL_ICONS: Record<string, string> = {
  instagram: InstagramIcon,
  instagram_story: InstagramStoryIcon,
  tiktok: TikTokIcon,
  tik_tok: TikTokIcon,
  youtube: YouTubeIcon,
  twitter: XIcon,
  discord: DiscordIcon,
  twitch: TwitchIcon,
  snapchat: SnapchatIcon,
  facebook: FacebookIcon,
  telegram: TelegramIcon,
  linkedin: LinkedInIcon,
  custom: LinkIcon,
};

export const SOCIAL_LABELS: Record<string, string> = {
  instagram: "Instagram",
  instagram_story: "Instagram Story",
  tiktok: "TikTok",
  tik_tok: "TikTok",
  youtube: "YouTube",
  twitter: "X",
  discord: "Discord",
  twitch: "Twitch",
  snapchat: "Snapchat",
  facebook: "Facebook",
  telegram: "Telegram",
  linkedin: "LinkedIn",
  custom: "Custom",
};

export const SocialProviders = [
  {
    label: "Instagram",
    value: SocialProvider.Instagram,
    icon: InstagramIcon,
  },
  {
    label: "TikTok",
    value: SocialProvider.TikTok,
    icon: TikTokIcon,
  },
  { label: "YouTube", value: SocialProvider.Youtube, icon: YouTubeIcon },
  { label: "X", value: SocialProvider.Twitter, icon: XIcon },
  {
    label: "Discord",
    value: SocialProvider.Discord,
    icon: DiscordIcon,
  },
  {
    label: "Twitch",
    value: SocialProvider.Twitch,
    icon: TwitchIcon,
  },
  {
    label: "Snapchat",
    value: SocialProvider.Snapchat,
    icon: SnapchatIcon,
  },
  {
    label: "Facebook",
    value: SocialProvider.Facebook,
    icon: FacebookIcon,
  },
  {
    label: "Telegram",
    value: SocialProvider.Telegram,
    icon: TelegramIcon,
  },
  {
    label: "LinkedIn",
    value: SocialProvider.Linkedin,
    icon: LinkedInIcon,
  },
];

/**
 * Make sure regex is adjusted on back-end
 */
export const REGEX_BY_PROVIDER = {
  instagram: /.*instagram\.com\/([._a-z\d]+)\/?(?:[?#]\S*)?(?=$|[\s<>)!,?]|\])/i,
  tiktok:
    /https\:\/\/www\.tiktok\.com\/@([._a-z\d]+)\/?(?:[?#]\S*)?(?=$|[\s<>)!,?]|\])/i,
  // YT regex: \u0600-\u06FF allow to add url with arabic characters
  youtube:
    /https\:\/\/(?:www\.|m\.)?youtube\.com\/(?:c\/|channel\/|user\/|@)([._\-a-z\u0600-\u06FFąćęłńóśźżĄĆĘŁŃÓŚŻŹ%\d]+)\/?(?:[?#]\S*)?(?=$|[\s<>)!,?]|\])/i,
  twitter:
    /https\:\/\/(?:www\.)?(?:twitter|x)\.com\/(?!home\/?|explore\/?|i\/|intent\/|share\/?|search\/?)([._a-z\d]+)\/?(?:[?#]\S*)?(?=$|[\s<>)!,?]|\])/i,
  discord:
    /https\:\/\/discord\.com\/(?!channels(?:[/?#]|$)|invite(?:[/?#]|$))([._a-z\d-]+)\/?(?:[?#]\S*)?(?=$|[\s<>)!,?]|\])/i,
  twitch:
    /https\:\/\/www\.twitch\.tv\/(?!videos(?:[/?#]|$)|directory(?:[/?#]|$)|downloads(?:[/?#]|$)|jobs(?:[/?#]|$)|p(?:[/?#]|$))([._a-z\d]+)\/?(?:[?#]\S*)?(?=$|[\s<>)!,?]|\])/i,
  snapchat:
    /https\:\/\/.*snapchat\.com\/(?:add\/|p\/|@)([-._a-zA-Z\d]+)\/?(?:[?#]\S*)?(?=$|[\s<>)!,?]|\])/i,
  facebook:
    /https:\/\/(?:www\.)?(?:facebook\.com|fb\.me)\/(?!groups(?:[/?#]|$)|watch(?:[/?#]|$)|share(?:[/?#]|$)|reel(?:[/?#]|$)|videos(?:[/?#]|$)|photo(?:[/?#]|$)|photo\.php(?:[?#]|$)|events(?:[/?#]|$)|marketplace(?:[/?#]|$))(?:profile\.php\?id=)?([._\-a-zA-Z\d]+)\/?(?:[?#]\S*)?(?=$|[\s<>)!,?]|\])/i,
  telegram:
    /https\:\/\/t\.me\/([._a-z\d+-]+)\/?(?:[?#]\S*)?(?=$|[\s<>)!,?]|\])/i,
  linkedin:
    /https?:\/\/(?:[a-z]{2,3}\.)?(?:www\.)?linkedin\.com\/(?:in)\/([^/?#]+)\/?(?:[?#]\S*)?(?=$|[\s<>)!,?]|\])/i,
};

export const CUSTOM_URL_REGEX = /^https?:\/\/(?!.*:\/\/)\S+/;

export enum SocialProfileStatus {
  Fresh = 0,
  Verified = 1,
  Error = 2,
  NotFound = 3,
}

export enum SocialProfileFlags {
  Private = "PRIVATE",
  Restricted = "RESTRICTED",
  ApiPaired = "API_PAIRED",
}

export const isFacebookNumericUsername = (username: string) => {
  return /^\d+$/.test(username);
};
/* SOCIAL PROVIDERS DECLARATION ABOVE */

export type XOR<T, U> =
  | (T & { [K in keyof U]?: never })
  | (U & { [K in keyof T]?: never });

export type CustomSelectOption<T> = {
  value: T;
  label: ReactNode | string;
};

export const convertTikTokToWorkspaceVersion = (
  socialProvider: string | null,
) => {
  return socialProvider?.replace("tik_tok", "tiktok") ?? null;
};

export const convertTikTokToTagmeVersion = (socialProvider: string | null) => {
  return socialProvider?.replace("tiktok", "tik_tok") ?? null;
};
